/**
 * When a peptide order ships.
 *
 * The pharmacy compounds, ships Monday–Thursday, and an order placed after
 * their cut-off starts the next shipping day. A Thursday afternoon order ships
 * the following Monday — which is how a patient ends up travelling without
 * their prescription if staff assume "today".
 *
 * SHIP DATE ONLY. Overnight shipping is no longer offered (2026-09-25, Adan),
 * and no other service commits to a transit time, so there is no arrival
 * date we could stand behind — an invented one reads exactly like a promise.
 *
 * THE CUT-OFF IS NOON EASTERN, WHEREVER THE CLINIC IS.
 *
 * It is the pharmacy's compounding day that has to start, not the clinic's, so
 * the deadline is one instant for everybody. Mark's true cut-off is 2 PM ET;
 * noon is the number he asked us to promise, because we hold every order for an
 * hour before sending it and he wanted that margin real rather than
 * theoretical.
 *
 * The clinic's timezone decides only how that instant is DISPLAYED — Los
 * Angeles reads "9:00 AM", New York reads "12:00 PM", and both mean the same
 * moment. This is the trap the whole module exists around: a west-coast clinic
 * can miss the deadline before 10 in the morning, with its day barely started.
 *
 * Saturday delivery is deliberately NOT modelled yet. The pharmacy will accept
 * it as a per-address opt-in, and until that exists a Friday order is honestly
 * described as shipping Monday.
 */

import { toLocaleTimeStringInTz } from '@/lib/facilityTimezone';

/** Noon, Eastern. The promised cut-off, already carrying the pharmacy's margin. */
const CUTOFF_HOUR_ET = 12;

/** Days the pharmacy ships. Friday is excluded until Saturday delivery exists. */
const SHIPPING_DAYS = [1, 2, 3, 4]; // Mon–Thu

/**
 * US federal holidays the carrier does not move on.
 *
 * Hardcoded and dated rather than computed: a wrong rule silently shifts every
 * estimate around it, and a list that runs out is visibly wrong instead. Extend
 * it yearly — the fallback while it is stale is an estimate one day optimistic,
 * not a crash.
 */
const HOLIDAYS = new Set([
  '2026-01-01', '2026-01-19', '2026-02-16', '2026-05-25', '2026-06-19',
  '2026-07-03', '2026-09-07', '2026-10-12', '2026-11-11', '2026-11-26',
  '2026-12-25',
  '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31', '2027-06-18',
  '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25',
  '2027-12-24',
]);

/** The calendar parts of an instant, read in a specific timezone. */
function partsIn(at: Date, timeZone: string): { y: number; m: number; d: number; hour: number; weekday: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hour12: false, weekday: 'short',
  });
  const parts = Object.fromEntries(fmt.formatToParts(at).map((p) => [p.type, p.value]));
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return {
    y: Number(parts.year),
    m: Number(parts.month),
    d: Number(parts.day),
    // "24" at midnight in some locales; normalise so comparisons hold.
    hour: Number(parts.hour) % 24,
    weekday: weekdays.indexOf(String(parts.weekday)),
  };
}

const iso = (y: number, m: number, d: number): string =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** A date the pharmacy ships on: a weekday in Mon–Thu that is not a holiday. */
function isShippingDay(day: Date): boolean {
  const key = iso(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate());

  return SHIPPING_DAYS.includes(day.getUTCDay()) && !HOLIDAYS.has(key);
}

const addDays = (d: Date, n: number): Date => new Date(d.getTime() + n * 86_400_000);

export interface ShippingEstimate {
  /** True when the order still makes today's pharmacy run. */
  beforeCutoff: boolean;
  /**
   * True only when beating the cut-off actually changes the outcome — today is
   * a shipping day AND it has not passed. On a Friday the deadline is real but
   * meaningless: the order leaves Monday whether it is placed early or late.
   */
  cutoffMatters: boolean;
  /** The cut-off rendered on the clinic's clock, e.g. "9:00 AM" in Los Angeles. */
  cutoffLocal: string;
  /** Ship date as a UTC-midnight anchor, for formatting. */
  shipDay: Date;
}

/**
 * When an order placed `at` ships.
 *
 * `timeZone` only decides how the cut-off is DISPLAYED; the cut-off itself is
 * evaluated in Eastern, which is what the pharmacy runs on.
 */
export function shippingEstimate(at: Date, timeZone: string): ShippingEstimate {
  const et = partsIn(at, 'America/New_York');
  const beforeCutoff = et.hour < CUTOFF_HOUR_ET;

  // Anchor on the PHARMACY's calendar day, at UTC midnight, so day arithmetic
  // never drifts across a DST boundary.
  const today = new Date(Date.UTC(et.y, et.m - 1, et.d));
  let ship = today;
  // Missed today's run, or today is not a shipping day at all → the next one.
  if (!beforeCutoff || !isShippingDay(ship)) {
    do {
      ship = addDays(ship, 1);
    } while (!isShippingDay(ship));
  }

  return {
    beforeCutoff,
    cutoffMatters: beforeCutoff && isShippingDay(today),
    cutoffLocal: cutoffInZone(et, timeZone),
    shipDay: ship,
  };
}

/**
 * Noon Eastern on the pharmacy's current day, read on the clinic's clock.
 *
 * Found by probing rather than by assuming an offset: Eastern is UTC-4 or -5
 * depending on the season, and hardcoding either puts the deadline an hour out
 * for half the year — on a message whose entire job is a deadline.
 */
function cutoffInZone(et: { y: number; m: number; d: number }, timeZone: string): string {
  let instant = new Date(Date.UTC(et.y, et.m - 1, et.d, CUTOFF_HOUR_ET + 4));
  const seen = partsIn(instant, 'America/New_York').hour;
  if (seen !== CUTOFF_HOUR_ET) {
    instant = new Date(instant.getTime() + (CUTOFF_HOUR_ET - seen) * 3_600_000);
  }

  return toLocaleTimeStringInTz(instant, 'en-US', {
    timeZone, hour: 'numeric', minute: '2-digit',
  });
}

/** "Wed, Sep 16" for a UTC-midnight day anchor. */
export function formatDay(day: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric',
  }).format(day);
}

/**
 * The one line shown on the order form: the ship date, and the cut-off while
 * it can still be acted on. Once it has passed, saying so changes nothing —
 * the date already carries the consequence.
 *
 * No arrival date: overnight shipping is no longer offered, and nothing else
 * commits to a transit time (see the module comment).
 */
export function shippingSlaText(at: Date, timeZone: string): string {
  const e = shippingEstimate(at, timeZone);

  return `Ships ${formatDay(e.shipDay)}${e.cutoffMatters ? ` if ordered by ${e.cutoffLocal} today` : ''}.`;
}
